import React, {
	createContext,
	type Dispatch,
	type ReactNode,
	type SetStateAction,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from 'react';
import noop from 'lodash/noop';
import type { ActionMeta, OptionType } from '@atlaskit/select';
import { JiraProjectAri } from '@atlassian/ari/jira';
import {
	useExperienceFail,
	useExperienceStart,
	useExperienceSuccess,
} from '@atlassian/jira-business-experience-tracking/src/controllers/experience-tracker/index.tsx';
import type { PickedProject } from '@atlassian/jira-project-picker/src/model/index.tsx';
import { useConnectedProjects } from '@atlassian/jira-shared-software-dates/src/controllers/use-connected-projects/index.tsx';
import { useFetchConnectedProjects } from '@atlassian/jira-shared-software-dates/src/controllers/use-fetch-connected-projects/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import {
	CLEAR_JSW_PROJECTS_EXPERIENCE,
	CONNECT_JSW_PROJECT_EXPERIENCE,
	DISCONNECT_JSW_PROJECT_EXPERIENCE,
	FETCH_CONNECTED_JSW_PROJECTS_EXPERIENCE,
} from '../../common/constants.tsx';
import {
	deleteProjectProperty,
	setProjectProperty,
} from '../../services/project-properties/index.tsx';
import type { ConnectedProject } from '../../types.tsx';
import type { UseChangeConnectedProjectsReturnType } from './types.tsx';
import { useConnectedProjectMigrationToAgs } from './use-connected-project-migration-to-ags/index.tsx';
import { getLastConnectedProject } from './utils.tsx';

export type State = {
	connectedProjects: ConnectedProject[] | undefined;
	loading: boolean;
	error: Error | null;
};

type Context = State & {
	setContext: Dispatch<SetStateAction<State>>;
};

export const ConnectedProjectsContext = createContext<Context | null>(null);

const useConnectedProjectsContext = (): Context => {
	const context = useContext(ConnectedProjectsContext);

	if (!context) {
		throw new Error('useConnectedProjects must be used inside a ConnectedProjectsProvider');
	}

	return context;
};

export const ConnectedProjectsProvider = ({ children }: { children: ReactNode }) => {
	const [state, setState] = useState<State>({
		connectedProjects: undefined,
		loading: true,
		error: null,
	});

	const value = useMemo(() => ({ ...state, setContext: setState }), [state]);

	return (
		<ConnectedProjectsContext.Provider value={value}>{children}</ConnectedProjectsContext.Provider>
	);
};

export const useGetConnectedProjects = (projectId: string) => {
	const { connectedProjects, error, loading, setContext } = useConnectedProjectsContext();
	const {
		fetchConnectedProjects,
		loading: loadingGql,
		error: errorGql,
	} = useFetchConnectedProjects();
	const {
		shouldFetchFromRestWithoutMigration,
		migrateConnectedProjectPropertyIfNecessary,
		fetchConnectedProjectFromRest,
	} = useConnectedProjectMigrationToAgs(projectId);
	const startExperience = useExperienceStart(FETCH_CONNECTED_JSW_PROJECTS_EXPERIENCE);
	const onExperienceSuccess = useExperienceSuccess(FETCH_CONNECTED_JSW_PROJECTS_EXPERIENCE);
	const onExperienceFail = useExperienceFail(FETCH_CONNECTED_JSW_PROJECTS_EXPERIENCE);

	useEffect(() => {
		// We are deliberately checking for `undefined` as we are setting connectedProject to `undefined` in the React context
		// to force refetch after a successful connect/disconnect
		if (connectedProjects !== undefined) {
			return;
		}

		(async () => {
			let connectedProjectsAgs: ConnectedProject[] = [];
			const setContextOnFailure = (err: Error) =>
				setContext({ connectedProjects: [], loading: false, error: err });

			let shouldFetchConnectedProjectsFromRest = false;

			try {
				shouldFetchConnectedProjectsFromRest = await shouldFetchFromRestWithoutMigration();
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (err: any) {
				setContextOnFailure(err);
				onExperienceFail('Failed to check if connected project property is migrated', err);
				return;
			}

			if (shouldFetchConnectedProjectsFromRest) {
				let connectedProjectsRest: ConnectedProject | undefined;

				startExperience();
				try {
					connectedProjectsRest = await fetchConnectedProjectFromRest();
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
				} catch (err: any) {
					setContextOnFailure(err);
					onExperienceFail('Failed to fetch connected project from REST', err);
					return;
				}
				onExperienceSuccess();

				setContext({
					connectedProjects: connectedProjectsRest ? [connectedProjectsRest] : [],
					loading: false,
					error: null,
				});

				return;
			}

			startExperience();
			try {
				connectedProjectsAgs = await fetchConnectedProjects(projectId);
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (err: any) {
				setContextOnFailure(err);
				onExperienceFail('Failed to fetch connected projects from AGS', err);
				return;
			}
			onExperienceSuccess();

			let migratedProject: ConnectedProject | undefined;

			try {
				migratedProject = await migrateConnectedProjectPropertyIfNecessary();
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (err: any) {
				setContextOnFailure(err);
				return;
			}

			setContext({
				/**
				 * Since "connectedProjects" dependency of this effect is "undefined" already,
				 * We're setting "connectedProjects" here without re-fetching them from Relay's cache
				 * */
				connectedProjects: migratedProject ? [migratedProject] : connectedProjectsAgs,
				loading: false,
				error: null,
			});
		})();
	}, [
		connectedProjects,
		fetchConnectedProjects,
		onExperienceFail,
		onExperienceSuccess,
		projectId,
		setContext,
		migrateConnectedProjectPropertyIfNecessary,
		startExperience,
		shouldFetchFromRestWithoutMigration,
		fetchConnectedProjectFromRest,
	]);

	return {
		connectedProjects,
		error: error || errorGql,
		loading: loading || loadingGql,
	};
};

export const useChangeConnectedProjects = (
	projectId: string,
): UseChangeConnectedProjectsReturnType => {
	const { connectedProjects, loading, error, setContext } = useConnectedProjectsContext();

	const cloudId = useCloudId();

	const startConnectExperience = useExperienceStart(CONNECT_JSW_PROJECT_EXPERIENCE);
	const onSuccessConnect = useExperienceSuccess(CONNECT_JSW_PROJECT_EXPERIENCE);
	const onFailConnect = useExperienceFail(CONNECT_JSW_PROJECT_EXPERIENCE);

	const startDisconnectExperience = useExperienceStart(DISCONNECT_JSW_PROJECT_EXPERIENCE);
	const onSuccessDisconnect = useExperienceSuccess(DISCONNECT_JSW_PROJECT_EXPERIENCE);
	const onFailDisconnect = useExperienceFail(DISCONNECT_JSW_PROJECT_EXPERIENCE);

	const startClearExperience = useExperienceStart(CLEAR_JSW_PROJECTS_EXPERIENCE);
	const onSuccessClear = useExperienceSuccess(CLEAR_JSW_PROJECTS_EXPERIENCE);
	const onFailClear = useExperienceFail(CLEAR_JSW_PROJECTS_EXPERIENCE);

	const {
		onProjectConnect,
		onProjectDisconnect,
		onDisconnectAllProjects,
		loading: loadingGql,
	} = useConnectedProjects(projectId);

	const connectProject = useCallback(
		async (projectToConnect: PickedProject) => {
			if (!projectToConnect) {
				return;
			}

			const newlyConnectedProject = {
				id: projectToConnect.value,
				resourceId: JiraProjectAri.create({
					siteId: cloudId,
					projectId: projectToConnect.value,
				}).toString(),
				name: projectToConnect.name,
				avatarUrl: projectToConnect.avatarUrl,
			};

			startConnectExperience();
			setContext({
				connectedProjects,
				loading: true,
				error: null,
			});

			try {
				// We intentionally are not waiting result of REST API call after switching to GraphQL
				setProjectProperty({
					propertyValue: newlyConnectedProject,
					projectKeyOrId: projectId,
					// Errors from REST API are handled and reported as part of the call already,
					// Here's we're avoiding runtime to complain about uncaught errors
				}).catch(noop);

				await onProjectConnect({
					id: newlyConnectedProject.id,
					name: newlyConnectedProject.name,
					avatarUrl: newlyConnectedProject.avatarUrl,
				});

				setContext({
					loading: false,
					error: null,
					// we are setting connectedProject to undefined thereby forcing refetch from Relay store
					connectedProjects: undefined,
				});

				onSuccessConnect();
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (err: any) {
				setContext({
					loading: false,
					error: err,
					connectedProjects,
				});
				onFailConnect('Failed to connect project', err);
				throw err;
			}
		},
		[
			cloudId,
			connectedProjects,
			onFailConnect,
			onSuccessConnect,
			projectId,
			setContext,
			startConnectExperience,
			onProjectConnect,
		],
	);

	const disconnectProject = useCallback(
		async (projectToDisconnect: PickedProject, actionMeta?: ActionMeta<OptionType>) => {
			if (!projectToDisconnect) {
				return;
			}

			startDisconnectExperience();

			try {
				// We intentionally are not waiting result of REST API call after switching to GraphQL
				// Errors from REST API are handled and reported as part of the call already,
				// Here's we're avoiding runtime to complain about uncaught errors
				// if we are disconnecting the last project, we delete the property otherwise we set it with the last connected project
				const lastConnectedProjectToSetInProjectProperty = getLastConnectedProject(
					connectedProjects,
					projectToDisconnect.value,
				);
				if (lastConnectedProjectToSetInProjectProperty) {
					setProjectProperty({
						propertyValue: lastConnectedProjectToSetInProjectProperty,
						projectKeyOrId: projectId,
					}).catch(noop);
				} else {
					deleteProjectProperty({ projectKeyOrId: projectId }).catch(noop);
				}

				await onProjectDisconnect(
					{
						id: projectToDisconnect.value,
						name: projectToDisconnect.name ?? '',
					},
					{
						onClientError: () => {
							onSuccessDisconnect();
						},
						actionMeta,
					},
				);

				setContext({
					loading: false,
					error: null, // we are setting connectedProject to undefined thereby forcing refetch from Relay store
					connectedProjects: undefined,
				});
				onSuccessDisconnect();
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (err: any) {
				setContext({ loading: false, error: err, connectedProjects });
				onFailDisconnect('Failed to disconnect project', err);
				throw err;
			}
		},
		[
			startDisconnectExperience,
			connectedProjects,
			setContext,
			onSuccessDisconnect,
			projectId,
			onFailDisconnect,
			onProjectDisconnect,
		],
	);

	const disconnectAllProjects = useCallback(async () => {
		if (!connectedProjects) {
			return;
		}

		startClearExperience();
		try {
			deleteProjectProperty({ projectKeyOrId: projectId }).catch(noop);

			await onDisconnectAllProjects(connectedProjects);

			setContext({
				loading: false,
				error: null,
				connectedProjects: undefined, // we are setting connectedProject to undefined thereby forcing refetch from Relay store
			});
			onSuccessClear();
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (err: any) {
			setContext({ loading: false, error: err, connectedProjects });
			onFailClear('Failed to clear connected projects', err);
			throw err;
		}
	}, [
		connectedProjects,
		onDisconnectAllProjects,
		onFailClear,
		onSuccessClear,
		projectId,
		setContext,
		startClearExperience,
	]);

	return {
		loading: loading || loadingGql,
		error,
		onProjectConnect: connectProject,
		onProjectDisconnect: disconnectProject,
		onDisconnectAllProjects: disconnectAllProjects,
		connectedProjects,
	};
};
